import {
	AlignJustify,
	ArrowLeftRight,
	ChevronDown,
	ChevronsDownUp,
	ChevronsUpDown,
	ChevronUp,
	Clipboard,
	Columns2,
	Copy,
	Edit3,
	Eye,
	List,
	Lock,
	type LucideIcon,
	MinusCircle,
	MoveHorizontal,
	Plus,
	PlusCircle,
	Settings,
	Type,
	Unlock,
	WholeWord,
	WrapText,
	X,
} from "lucide-react";
import {
	type ComponentProps,
	createContext,
	memo,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

/* ═══ Types ═══════════════════════════════════ */
type LineType = "equal" | "added" | "removed" | "spacer";
type LineBlockPos = "start" | "middle" | "end" | "single";
type DiffPrecision = "word" | "letter";
type DiffLayout = "split" | "unified";

const PanelSides = ["left", "right"] as const;
type PanelSide = (typeof PanelSides)[number];

type Tab = { id: string; left: string; right: string };
type RenderItem =
	| { kind: "row"; idx: number; side?: PanelSide }
	| { kind: "fold"; idx: number; count: number };
type Segment = { id: string | number; text: string; hl: boolean };
type DiffLine = {
	text: string;
	type: LineType;
	num: number | null;
	segs?: Segment[];
};
type DiffResult = {
	left: DiffLine[];
	right: DiffLine[];
	added: number;
	removed: number;
};
type Block = { start: number; end: number };

/* ═══ Constants ════════════════════════════════ */
const CONTEXT = 3;
const FOLD_THRESHOLD = 20;
const TAB_TITLE_MAX = 18;
const EMPTY_TAB: Tab = { id: "", left: "", right: "" };

/* ═══ TabsContext ═════════════════════════════ */
const [TabsProviderComp, useTabs] = createSafeContext<{
	tabs: Tab[];
	activeTabId: string;
	activeTab: Tab;
	setActiveTab: (id: string) => void;
	addTab: () => void;
	closeTab: (id: string) => void;
	closeAllTabs: () => void;
	updateText: (side: PanelSide, value: string) => void;
	swap: () => void;
}>("tabs");

const TabsProvider = ({ children }: { children: ReactNode }) => {
	const [tabs, setTabs] = usePersistedState<Tab[]>("diff:tabs", () => [
		{ ...EMPTY_TAB, id: genId() },
	]);
	const [activeTabId, setActiveTabId] = usePersistedState<string>(
		"diff:activeTab",
		() => tabs[0]?.id ?? "",
	);

	const activeTab =
		tabs.find((t) => t.id === activeTabId) ?? tabs[0] ?? EMPTY_TAB;

	const updateText = useCallback(
		(side: PanelSide, value: string) => {
			setTabs((prev) =>
				prev.map((t) => (t.id === activeTabId ? { ...t, [side]: value } : t)),
			);
		},
		[activeTabId, setTabs],
	);

	const swap = useCallback(() => {
		setTabs((prev) =>
			prev.map((t) =>
				t.id === activeTabId ? { ...t, left: t.right, right: t.left } : t,
			),
		);
	}, [activeTabId, setTabs]);

	const addTab = useCallback(() => {
		const id = genId();
		setTabs((prev) => [...prev, { id, left: "", right: "" }]);
		setActiveTabId(id);
	}, [setTabs, setActiveTabId]);

	const closeTab = useCallback(
		(id: string) => {
			setTabs((prev) => {
				if (prev.length === 1) return prev;
				const idx = prev.findIndex((t) => t.id === id);
				const next = prev.filter((t) => t.id !== id);
				setActiveTabId((current) => {
					if (current !== id) return current;
					const newIdx = Math.min(idx, next.length - 1);
					return next[newIdx]?.id ?? "";
				});
				return next;
			});
		},
		[setTabs, setActiveTabId],
	);

	const closeAllTabs = useCallback(() => {
		setTabs(() => {
			const id = genId();
			setActiveTabId(id);
			return [{ ...EMPTY_TAB, id }];
		});
	}, [setTabs, setActiveTabId]);

	const value = useMemo(
		() =>
			({
				tabs,
				activeTabId,
				activeTab,
				setActiveTab: setActiveTabId,
				addTab,
				closeTab,
				closeAllTabs,
				updateText,
				swap,
			}) satisfies ReturnType<typeof useTabs>,
		[
			tabs,
			activeTabId,
			activeTab,
			addTab,
			closeTab,
			closeAllTabs,
			updateText,
			swap,
			setActiveTabId,
		],
	);

	return <TabsProviderComp value={value}>{children}</TabsProviderComp>;
};

/* ═══ SettingsContext ═════════════════════════ */
type SettingsState = {
	diffMode: boolean;
	diffPrecision: DiffPrecision;
	diffLayout: DiffLayout;
	lineWrap: boolean;
	scrollLock: boolean;
};
type SettingsSetter = <K extends keyof SettingsState>(
	key: K,
	value: SettingsState[K],
) => void;

const [SettingsProviderComp, useSettings] = createSafeContext<
	SettingsState & { set: SettingsSetter }
>("settings");

const SettingsProvider = ({ children }: { children: ReactNode }) => {
	const [state, setState] = usePersistedState<SettingsState>("diff:settings", {
		diffMode: true,
		diffPrecision: "word",
		diffLayout: "split",
		lineWrap: false,
		scrollLock: true,
	});

	const set = useCallback<SettingsSetter>(
		(key, value) => setState((prev) => ({ ...prev, [key]: value })),
		[setState],
	);

	const value = useMemo(
		() => ({ ...state, set }) satisfies ReturnType<typeof useSettings>,
		[state, set],
	);

	return <SettingsProviderComp value={value}>{children}</SettingsProviderComp>;
};

/* ═══ DiffContext ═════════════════════════════ */
const [DiffProviderComp, useDiff] = createSafeContext<{
	diff: DiffResult;
	groups: Block[];
	currentGroupIndex: number;
	activeGroup: Block | null;
	setCurrentChange: (idx: number) => void;
}>("diff");

const DiffProvider = ({ children }: { children: ReactNode }) => {
	const { activeTab } = useTabs();
	const { diffPrecision } = useSettings();
	const [currentChange, setCurrentChange] = useState(0);

	const diff = useMemo(
		() => computeDiff(activeTab.left, activeTab.right, diffPrecision),
		[activeTab.left, activeTab.right, diffPrecision],
	);
	const groups = useMemo(
		() => findBlocks(diff.left, (t) => t !== "equal"),
		[diff.left],
	);
	const currentGroupIndex = Math.min(
		currentChange,
		Math.max(0, groups.length - 1),
	);
	const activeGroup = groups[currentGroupIndex] ?? null;

	const value = useMemo(
		() =>
			({
				diff,
				groups,
				currentGroupIndex,
				activeGroup,
				setCurrentChange,
			}) satisfies ReturnType<typeof useDiff>,
		[diff, groups, currentGroupIndex, activeGroup],
	);

	return <DiffProviderComp value={value}>{children}</DiffProviderComp>;
};

/* ═══ FoldContext ═════════════════════════════ */
const [FoldProviderComp, useFold] = createSafeContext<{
	foldable: Block[];
	renderItems: RenderItem[];
	allCollapsed: boolean;
	toggleAll: () => void;
	toggleBlock: (idx: number) => void;
}>("fold");

const FoldProvider = ({ children }: { children: ReactNode }) => {
	const { diff } = useDiff();
	const { diffLayout } = useSettings();
	const [expanded, setExpanded] = useState<Set<number>>(new Set());

	const foldable = useMemo(
		() => findBlocks(diff.left, (t) => t === "equal", FOLD_THRESHOLD),
		[diff.left],
	);
	const renderItems = useMemo(
		() => buildRenderItems(diff, foldable, expanded, diffLayout),
		[diff, foldable, expanded, diffLayout],
	);

	const value = useMemo(
		() =>
			({
				foldable,
				renderItems,
				allCollapsed: expanded.size === 0,
				toggleBlock: (idx: number) =>
					setExpanded((prev) => {
						const n = new Set(prev);
						n.has(idx) ? n.delete(idx) : n.add(idx);
						return n;
					}),
				toggleAll: () =>
					setExpanded((prev) =>
						prev.size === 0 ? new Set(foldable.map((_, i) => i)) : new Set(),
					),
			}) satisfies ReturnType<typeof useFold>,
		[foldable, renderItems, expanded],
	);

	return <FoldProviderComp value={value}>{children}</FoldProviderComp>;
};

/* ═══ Hooks ════════════════════════════ */
function usePersistedState<T>(key: string, init: T | (() => T)) {
	const [state, setState] = useState<T>(() => {
		try {
			const raw = localStorage?.getItem(key);
			if (raw != null) return JSON.parse(raw) as T;
		} catch {}
		return typeof init === "function" ? (init as () => T)() : init;
	});

	useEffect(() => {
		try {
			localStorage?.setItem(key, JSON.stringify(state));
		} catch {}
	}, [key, state]);

	return [state, setState] as const;
}

const usePanelActions = (side: PanelSide) => {
	const { activeTab, updateText } = useTabs();
	const { set } = useSettings();
	const other = side === "left" ? "right" : "left";

	return {
		paste: async () => {
			const clipText = await navigator.clipboard.readText();
			if (clipText) {
				updateText(side, clipText);
				if (activeTab[other]) set("diffMode", true);
			}
		},
		copy: async () => {
			await navigator.clipboard.writeText(activeTab[side]);
		},
		clear: () => {
			updateText(side, "");
			if (!activeTab[other]) set("diffMode", false);
		},
	};
};

function useSyncScroll(
	refA: RefObject<HTMLElement | null> | undefined,
	refB: RefObject<HTMLElement | null> | undefined,
	axis: "x" | "y" | "both" = "both",
	active = true,
) {
	useEffect(() => {
		const a = refA?.current;
		const b = refB?.current;
		if (!a || !b || !active) return;

		const syncAxis = (
			src: HTMLElement,
			dst: HTMLElement,
			pos: "scrollLeft" | "scrollTop",
			size: "scrollWidth" | "scrollHeight",
			client: "clientWidth" | "clientHeight",
		) => {
			const max = src[size] - src[client];
			const dstMax = dst[size] - dst[client];
			if (max > 0 && dstMax > 0) dst[pos] = (src[pos] / max) * dstMax;
		};

		const sync = (src: HTMLElement, dst: HTMLElement) => {
			if (axis !== "y")
				syncAxis(src, dst, "scrollLeft", "scrollWidth", "clientWidth");
			if (axis !== "x")
				syncAxis(src, dst, "scrollTop", "scrollHeight", "clientHeight");
		};

		const controller = new AbortController();
		const { signal } = controller;
		a.addEventListener("scroll", () => sync(a, b), { signal });
		b.addEventListener("scroll", () => sync(b, a), { signal });
		return () => controller.abort();
	}, [refA, refB, axis, active]);
}

/* ═══ Components ══════════════════════════════ */
const IconButton = ({
	icon: Icon,
	children,
	textOnly = false,
	endDecoration,
	className,
	...props
}: ComponentProps<"button"> & {
	icon?: LucideIcon;
	textOnly?: boolean;
	endDecoration?: ReactNode;
}) => (
	<button
		type="button"
		{...props}
		className={cn(
			"inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 font-medium text-gray-400 text-xs transition-colors hover:bg-gray-100 hover:text-gray-600",
			className,
		)}
	>
		{Icon && (
			<span className={cn(textOnly && children && "md:hidden")}>
				<Icon size={13} />
			</span>
		)}
		{children &&
			(Icon ? <span className="hidden md:inline">{children}</span> : children)}
		{endDecoration}
	</button>
);

const Toggle = ({
	checked,
	onChange,
	offIcon: OffIcon,
	onIcon: OnIcon,
	offLabel,
	onLabel,
	description,
	className,
}: {
	checked: boolean;
	onChange: (next: boolean) => void;
	offIcon: LucideIcon;
	onIcon: LucideIcon;
	offLabel: string;
	onLabel: string;
	description: ReactNode;
	className?: string;
}) => {
	const id = description ? `toggle-${offLabel}-${onLabel}` : undefined;
	return (
		<div className="flex items-center gap-2">
			<label
				htmlFor={id}
				className="cursor-pointer font-semibold text-gray-500 text-xs uppercase tracking-wide"
			>
				{description}
			</label>
			<button
				type="button"
				id={id}
				onClick={() => onChange(!checked)}
				aria-pressed={checked}
				className={cn(
					"relative grid min-h-8 shrink-0 cursor-pointer grid-cols-2 overflow-hidden rounded-full border border-gray-200 bg-gray-50 p-0.5 font-semibold text-xs",
					className,
				)}
			>
				<span
					className={cn(
						"absolute inset-0.5 w-1/2 rounded-full bg-white shadow transition-transform duration-200",
					)}
					style={{
						...(checked && { transform: "translateX(calc(100% - 4px))" }),
					}}
				/>
				{(
					[
						[OffIcon, offLabel, !checked],
						[OnIcon, onLabel, checked],
					] as const
				).map(([Icon, label, active]) => (
					<span
						key={label}
						className={cn(
							"relative inline-flex items-center justify-center gap-1 px-2.5 py-1 transition-colors duration-200",
							active ? "text-gray-700" : "text-gray-400",
						)}
					>
						<Icon size={12} />
						<span className="hidden md:inline">{label}</span>
					</span>
				))}
			</button>
		</div>
	);
};

const Tabs = ({ className, ...props }: ComponentProps<"div">) => {
	const { tabs, activeTabId, setActiveTab, addTab, closeTab } = useTabs();
	return (
		<div
			{...props}
			className={cn("flex min-w-0 items-center gap-1", className)}
		>
			{tabs.map((tab) => {
				const isActive = tab.id === activeTabId;
				const title = getTabTitle(tab);
				return (
					<button
						key={tab.id}
						type="button"
						onClick={() => setActiveTab(tab.id)}
						title={title}
						className={cn(
							"group relative inline-flex min-w-12 max-w-32 shrink cursor-pointer items-center gap-1 rounded-md border px-2 py-1.5 font-medium text-gray-400 text-xs transition-colors hover:bg-gray-100 hover:text-gray-600",
							tabs.length > 1 && "pr-6",
							isActive
								? "border-gray-200 text-gray-900"
								: "border-transparent text-gray-400",
						)}
					>
						<span className="truncate">{title}</span>
						{tabs.length > 1 && (
							<span
								onClick={(e) => {
									e.stopPropagation();
									closeTab(tab.id);
								}}
								className={cn(
									"absolute right-1 shrink-0 rounded p-1 text-gray-400 opacity-0 transition hover:text-gray-900 group-hover:opacity-100",
									isActive && "opacity-100",
								)}
							>
								<X size={12} />
							</span>
						)}
					</button>
				);
			})}
			<IconButton title="Add Tab" icon={Plus} onClick={addTab} />
		</div>
	);
};

const SwapButton = () => {
	const { swap } = useTabs();
	return (
		<button
			type="button"
			onClick={swap}
			className="absolute top-8 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border border-gray-200 bg-white p-2 text-gray-400 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-600"
		>
			<ArrowLeftRight size={16} />
		</button>
	);
};

const SettingsPanel = ({ open }: { open: boolean }) => {
	const { diffMode, diffLayout, diffPrecision, lineWrap, scrollLock, set } =
		useSettings();
	return (
		<div
			className={cn(
				"flex flex-wrap items-center justify-end gap-4 overflow-hidden border-gray-200 border-b bg-gray-50 px-4 transition-all duration-300 ease-in-out",
				open ? "max-h-24 py-2.5 opacity-100" : "max-h-0 opacity-0",
			)}
		>
			{diffMode && (
				<>
					<Toggle
						checked={diffLayout === "unified"}
						onChange={(v) => set("diffLayout", v ? "unified" : "split")}
						offIcon={Columns2}
						onIcon={AlignJustify}
						offLabel="Split"
						onLabel="Unified"
						description="Layout"
					/>
					<Toggle
						checked={diffPrecision === "letter"}
						onChange={(v) => set("diffPrecision", v ? "letter" : "word")}
						offIcon={WholeWord}
						onIcon={Type}
						offLabel="Word"
						onLabel="Letter"
						description="Precision"
					/>
				</>
			)}
			<Toggle
				checked={lineWrap}
				onChange={(v) => set("lineWrap", v)}
				offIcon={MoveHorizontal}
				onIcon={WrapText}
				offLabel="Scroll"
				onLabel="Wrap"
				description="Lines"
			/>
			<Toggle
				checked={scrollLock}
				onChange={(v) => set("scrollLock", v)}
				offIcon={Unlock}
				onIcon={Lock}
				offLabel="Free"
				onLabel="Sync"
				description="Scroll"
			/>
		</div>
	);
};

const PanelHeader = ({ side }: { side: PanelSide }) => {
	const { activeTab } = useTabs();
	const { diffMode } = useSettings();
	const { diff } = useDiff();
	const { paste, copy, clear } = usePanelActions(side);

	const Icon = side === "left" ? MinusCircle : PlusCircle;
	const changed = side === "left" ? diff.removed : diff.added;

	return (
		<div
			className={cn(
				"flex items-center gap-2 border-gray-200 border-b bg-gray-50 py-1.5 text-xs",
				side === "left" ? "pr-5 pl-3.5" : "pr-3.5 pl-5",
			)}
		>
			{diffMode && (
				<span
					className={cn(
						"inline-flex items-center gap-1 font-semibold",
						side === "left" ? "text-rose-600" : "text-emerald-600",
					)}
				>
					<Icon size={14} />
					<span>{changed}</span>
					<span className="hidden md:inline">
						{side === "left" ? "removals" : "additions"}
					</span>
				</span>
			)}
			<span className="inline-flex items-center gap-1 text-gray-500">
				<List size={14} />
				<span>{countLines(activeTab[side])}</span>
				<span className="hidden md:inline">lines</span>
			</span>
			<div className="ml-2 flex gap-1 border-l pl-2">
				<IconButton icon={Clipboard} onClick={paste}>
					Paste
				</IconButton>
				<IconButton textOnly icon={Copy} onClick={copy}>
					Copy
				</IconButton>
				<IconButton
					textOnly
					icon={X}
					className="hover:text-rose-500"
					onClick={clear}
				>
					Clear
				</IconButton>
			</div>
		</div>
	);
};

const DiffEditor = ({
	side,
	scrollRef,
}: {
	side: PanelSide;
	scrollRef?: RefObject<HTMLDivElement | null>;
}) => {
	const { activeTab, updateText } = useTabs();
	const { diffMode, lineWrap } = useSettings();
	const text = activeTab[side];
	const lineCount = Math.max(1, countLines(text));

	if (diffMode) return null;

	return (
		<div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
			<div className="flex min-h-full">
				<div
					className={cn(
						"w-11 shrink-0 select-none whitespace-pre bg-gray-50 py-3 pr-1.5 text-right font-mono text-gray-400 text-xs leading-5",
						!lineWrap && "sticky left-0 z-10",
					)}
				>
					{Array.from({ length: lineCount }, (_, i) => i + 1).join("\n")}
				</div>
				<div className="relative flex-1">
					<div
						aria-hidden="true"
						className={cn(
							"pointer-events-none invisible p-3 pb-8 font-mono text-xs leading-5",
							lineWrap
								? "wrap-break-word whitespace-pre-wrap"
								: "whitespace-pre",
						)}
					>
						{text}
					</div>
					<textarea
						value={text}
						onChange={(e) => updateText(side, e.target.value)}
						placeholder="Paste or type text here…"
						spellCheck={false}
						wrap={lineWrap ? "soft" : "off"}
						className="absolute inset-0 resize-none overflow-hidden border-none bg-white p-3 font-mono text-gray-800 text-xs leading-5 placeholder-gray-300 outline-none"
					/>
				</div>
			</div>
		</div>
	);
};

const ChangeNavigation = ({
	onNavigate,
}: {
	onNavigate: (delta: number) => void;
}) => {
	const { diffMode } = useSettings();
	const { groups, currentGroupIndex } = useDiff();
	return diffMode && groups.length ? (
		<div className="flex items-center gap-0.5">
			<IconButton icon={ChevronUp} onClick={() => onNavigate(-1)} />
			<span className="w-8 text-center font-mono font-semibold text-gray-500 text-xs tabular-nums">
				{currentGroupIndex + 1}/{groups.length}
			</span>
			<IconButton icon={ChevronDown} onClick={() => onNavigate(1)} />
		</div>
	) : null;
};

const FoldToggle = () => {
	const { diffMode } = useSettings();
	const { foldable, allCollapsed, toggleAll } = useFold();
	return diffMode && foldable.length ? (
		<div className="border-gray-200 border-l pl-1.5">
			<IconButton
				icon={allCollapsed ? ChevronsUpDown : ChevronsDownUp}
				onClick={toggleAll}
				className="p-0.5"
			/>
		</div>
	) : null;
};

const ScrollProxy = ({
	target,
	className,
	...props
}: { target?: RefObject<HTMLDivElement | null> } & ComponentProps<"div">) => {
	const proxyRef = useRef<HTMLDivElement>(null);
	const [innerWidth, setInnerWidth] = useState(0);

	const measure = useCallback(() => {
		const el = target?.current;
		setInnerWidth(el && el.scrollWidth > el.clientWidth ? el.scrollWidth : 0);
	}, [target]);

	useLayoutEffect(measure);

	useEffect(() => {
		const el = target?.current;
		if (!el) return;
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [target, measure]);

	useSyncScroll(proxyRef, target, "x");

	return (
		<div
			ref={proxyRef}
			{...props}
			className={cn("overflow-x-auto overflow-y-hidden", className)}
		>
			<div className="h-px" style={{ width: innerWidth }} />
		</div>
	);
};

const Gutter = ({
	num,
	className,
}: {
	num?: number | null;
	className?: string;
}) =>
	num === undefined ? null : (
		<div
			className={cn(
				"w-11 shrink-0 select-none pr-1.5 text-right font-mono text-xs",
				className,
				num !== null ? "text-gray-400" : "text-transparent",
			)}
		>
			{num ?? "·"}
		</div>
	);

const LINE_STYLES = {
	added: {
		background: ["bg-emerald-50", "bg-emerald-50", "bg-emerald-200"],
		border: ["border-emerald-300", "border-emerald-500"],
		gutter: ["bg-emerald-100", "bg-emerald-200"],
	},
	removed: {
		background: ["bg-rose-50", "bg-rose-50", "bg-rose-200"],
		border: ["border-rose-300", "border-rose-500"],
		gutter: ["bg-rose-100", "bg-rose-200"],
	},
	neutral: {
		background: ["bg-white", "bg-white"],
		border: ["border-gray-50", "border-gray-50"],
		gutter: ["bg-gray-50", "bg-gray-50"],
	},
};

const DiffRow = memo(
	({
		line: { type, segs, text },
		leftNum,
		rightNum,
		label,
		pos,
		isActive = false,
		lineWrap,
		gridRow,
		dataRow,
	}: {
		line: DiffLine;
		leftNum?: number | null;
		rightNum?: number | null;
		label?: string;
		pos?: LineBlockPos;
		isActive?: boolean;
		lineWrap: boolean;
		gridRow: number;
		dataRow: number;
	}) => {
		const styles =
			LINE_STYLES[type === "spacer" || type === "equal" ? "neutral" : type];
		const active = Number(isActive && type !== "equal");
		return (
			<div
				className={cn("flex", !lineWrap && "min-w-max")}
				style={{ gridRow }}
				data-row={dataRow}
			>
				<div
					className={cn(
						"flex shrink-0 items-end border-l-2",
						styles.border[active],
						styles.gutter[active],
						!lineWrap && "sticky left-0 z-10",
					)}
				>
					<Gutter num={leftNum} />
					<Gutter num={rightNum} />
				</div>
				<div className="flex min-w-0 flex-1 flex-col">
					{label && (
						<div className="text-end">
							<span className="sticky right-0 select-none py-0.5 pr-3 text-gray-400 text-xs italic">
								{label}
							</span>
						</div>
					)}
					<div
						style={{
							...(type === "spacer" && {
								backgroundImage:
									"repeating-linear-gradient(-45deg, #f3f4f6 0 1px, transparent 1px 4px)",
							}),
						}}
						className={cn(
							"flex flex-1 flex-col justify-end bg-fixed",
							styles.background[active],
							pos && "border-gray-400 border-x border-dotted",
							(pos === "start" || pos === "single") && "border-t",
							(pos === "end" || pos === "single") && "border-b",
						)}
					>
						<div
							className={cn(
								"min-h-5 px-3 font-mono text-gray-800 text-xs leading-5",
								lineWrap ? "whitespace-pre-wrap" : "whitespace-pre",
							)}
						>
							{segs
								? segs.map(({ id, hl, text }) => (
										<span key={id} className={cn(hl && styles.background[2])}>
											{text}
										</span>
									))
								: text}
						</div>
					</div>
				</div>
			</div>
		);
	},
);

const DiffRows = () => {
	const { diffMode, diffLayout, lineWrap, scrollLock } = useSettings();
	const { diff, groups, activeGroup, setCurrentChange } = useDiff();
	const { renderItems, toggleBlock } = useFold();
	const leftRef = useRef<HTMLDivElement>(null);
	const rightRef = useRef<HTMLDivElement>(null);

	useSyncScroll(leftRef, rightRef, "x", diffMode && scrollLock);

	const moveInfo = useMemo(() => {
		const leftLabels = new Map<number, string>();
		const rightLabels = new Map<number, string>();
		const leftPos = new Map<number, LineBlockPos>();
		const rightPos = new Map<number, LineBlockPos>();
		const fillPos = (
			start: number,
			end: number,
			map: Map<number, LineBlockPos>,
		) => {
			for (let i = start; i < end; i++) {
				const first = i === start,
					last = i === end - 1;
				map.set(
					i,
					first && last ? "single" : first ? "start" : last ? "end" : "middle",
				);
			}
		};
		for (const md of findMovedBlocks(diff)) {
			leftLabels.set(md.removalStart, `Moved to ${md.modifiedRange}`);
			rightLabels.set(md.additionStart, `Moved from ${md.originalRange}`);
			fillPos(md.removalStart, md.removalEnd, leftPos);
			fillPos(md.additionStart, md.additionEnd, rightPos);
		}
		return {
			left: { labels: leftLabels, pos: leftPos },
			right: { labels: rightLabels, pos: rightPos },
		};
	}, [diff]);

	const rowToGroup = useMemo(() => {
		const map = new Map<number, number>();
		groups.forEach(({ start, end }, i) => {
			for (let r = start; r < end; r++) map.set(r, i);
		});
		return map;
	}, [groups]);

	if (!diffMode) return null;

	const isSplit = diffLayout === "split";
	const panels: readonly PanelSide[] = isSplit ? PanelSides : ["left"];
	const panelRefs = [leftRef, rightRef] as const;
	const borderClass = (side: PanelSide) =>
		isSplit && side === "left" ? "border-gray-200 border-r" : "";

	const onPanelClick = (e: React.MouseEvent) => {
		const el = (e.target as Element).closest<HTMLElement>("[data-row]");
		if (!el) return;
		const gi = rowToGroup.get(Number(el.dataset.row));
		if (gi !== undefined) setCurrentChange(gi);
	};

	return (
		<>
			<div
				className={cn("isolate grid", isSplit ? "grid-cols-2" : "grid-cols-1")}
			>
				{panels.map((panelSide, col) => (
					<div
						key={panelSide}
						ref={panelRefs[col]}
						onClick={onPanelClick}
						className={cn(
							"grid min-w-0 auto-cols-auto grid-rows-subgrid",
							lineWrap
								? "overflow-hidden"
								: "overflow-x-auto overflow-y-hidden",
							borderClass(panelSide),
							col === 0 ? "col-start-1" : "col-start-2",
						)}
						style={{
							gridRow: `1 / span ${Math.max(1, renderItems.length)}`,
							...(!lineWrap && { scrollbarWidth: "none" }),
						}}
					>
						{renderItems.map((item, i) => {
							if (item.kind !== "row") return null;
							const row = item.idx;
							const leftLine = diff.left[row];
							const rightLine = diff.right[row];
							if (!leftLine || !rightLine) return null;
							const displaySide = isSplit ? panelSide : (item.side ?? "left");
							const line = displaySide === "left" ? leftLine : rightLine;
							return (
								<DiffRow
									key={`${row}${item.side ?? ""}`}
									gridRow={i + 1}
									dataRow={row}
									line={line}
									lineWrap={lineWrap}
									leftNum={
										isSplit
											? panelSide === "left"
												? line.num
												: undefined
											: item.side === "right"
												? null
												: leftLine.num
									}
									rightNum={
										isSplit
											? panelSide === "right"
												? line.num
												: undefined
											: item.side === "left"
												? null
												: rightLine.num
									}
									label={moveInfo[displaySide].labels.get(row)}
									pos={moveInfo[displaySide].pos.get(row)}
									isActive={
										!!activeGroup &&
										row >= activeGroup.start &&
										row < activeGroup.end
									}
								/>
							);
						})}
					</div>
				))}

				{renderItems.map((item, i) =>
					item.kind === "fold" ? (
						<button
							type="button"
							className="col-span-full flex w-full cursor-pointer items-center justify-center gap-2 border-gray-200 border-y bg-gray-50 px-4 py-1 font-medium text-gray-400 text-xs transition-colors hover:text-gray-500"
							key={`f${item.idx}`}
							style={{ gridRow: i + 1 }}
							onClick={() => toggleBlock(item.idx)}
						>
							<ChevronsUpDown size={12} />
							{item.count} lines
						</button>
					) : null,
				)}
			</div>
			{!lineWrap && (
				<div
					className={cn(
						"sticky bottom-0 z-10 grid",
						isSplit ? "grid-cols-2" : "grid-cols-1",
					)}
				>
					{panels.map((panelSide, col) => (
						<ScrollProxy
							key={panelSide}
							target={panelRefs[col]}
							className={borderClass(panelSide)}
						/>
					))}
				</div>
			)}
		</>
	);
};

/* ═══ Main Component ══════════════════════════ */
function DiffViewerInner() {
	const scrollRef = useRef<HTMLDivElement>(null);
	const isAutoScrolling = useRef(false);
	const scrollTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
	const leftEditorRef = useRef<HTMLDivElement>(null);
	const rightEditorRef = useRef<HTMLDivElement>(null);
	const editorRefs = [leftEditorRef, rightEditorRef] as const;
	const [settingsOpen, setSettingsOpen] = usePersistedState(
		"diff:settingsOpen",
		true,
	);
	const { diffMode, scrollLock, set } = useSettings();
	const { closeAllTabs } = useTabs();
	const { groups, currentGroupIndex, setCurrentChange } = useDiff();

	useSyncScroll(leftEditorRef, rightEditorRef, "both", !diffMode && scrollLock);

	const scrollToGroup = useCallback(
		(idx: number) => {
			const el = scrollRef.current,
				g = groups[idx];
			if (!el || !g) return;
			const row = el.querySelector(`[data-row="${g.start}"]`);
			if (!(row instanceof HTMLElement)) return;
			isAutoScrolling.current = true;
			el.scrollTo({
				top: Math.max(0, row.offsetTop - el.clientHeight / 3),
				behavior: "smooth",
			});
			setTimeout(() => {
				isAutoScrolling.current = false;
			}, 500);
		},
		[groups],
	);

	const navigateChange = useCallback(
		(delta: number) => {
			if (groups.length === 0) return;
			const next =
				(((currentGroupIndex + delta) % groups.length) + groups.length) %
				groups.length;
			setCurrentChange(next);
			scrollToGroup(next);
		},
		[groups, currentGroupIndex, setCurrentChange, scrollToGroup],
	);

	const handleScroll = useCallback(() => {
		clearTimeout(scrollTimer.current);
		scrollTimer.current = setTimeout(() => {
			if (isAutoScrolling.current || groups.length === 0) return;
			const el = scrollRef.current;
			if (!el) return;
			const viewMid = el.scrollTop + el.clientHeight / 3;
			let closest = 0,
				minDist = Infinity;
			for (let i = 0; i < groups.length; i++) {
				const g = groups[i];
				if (!g) continue;
				const row = el.querySelector(`[data-row="${g.start}"]`);
				if (!(row instanceof HTMLElement)) continue;
				const d = Math.abs(row.offsetTop + row.offsetHeight / 2 - viewMid);
				if (d < minDist) {
					minDist = d;
					closest = i;
				}
			}
			setCurrentChange(closest);
		}, 100);
	}, [groups, setCurrentChange]);

	return (
		<div className="flex h-screen flex-col bg-gray-100 font-sans">
			<header className="z-20 flex shrink-0 items-center justify-between border-gray-200 border-b bg-white px-4 py-2 shadow-sm">
				<div className="flex items-center">
					<Toggle
						description={
							<h2 className="shrink-0 font-bold text-gray-500 text-sm tracking-tight">
								<span className="font-black text-gray-900 text-lg">D</span>
								<span className="hidden md:inline">iff </span>
								<span className="font-black text-gray-900 text-lg">V</span>
								<span className="hidden md:inline">iewer</span>
							</h2>
						}
						checked={diffMode}
						onChange={(v) => set("diffMode", v)}
						offIcon={Edit3}
						onIcon={Eye}
						offLabel="Edit"
						onLabel="Diff"
					/>
					<Tabs className="ml-4 hidden border-gray-200 border-l pl-4 xl:flex" />
				</div>
				<div className="flex shrink-0 items-center gap-2">
					<ChangeNavigation onNavigate={navigateChange} />
					<FoldToggle />
					<IconButton
						icon={X}
						textOnly
						className="hover:text-rose-500"
						onClick={() => {
							closeAllTabs();
							set("diffMode", false);
						}}
					>
						Close all
					</IconButton>
					<IconButton
						icon={Settings}
						className={cn(settingsOpen && "bg-gray-100 text-gray-600")}
						onClick={() => setSettingsOpen((v) => !v)}
						endDecoration={
							<ChevronDown
								size={12}
								className={cn("transition", settingsOpen && "rotate-180")}
							/>
						}
					>
						Settings
					</IconButton>
				</div>
			</header>
			<SettingsPanel open={settingsOpen} />
			<Tabs className="flex-wrap border-gray-200 border-b bg-gray-50 px-4 py-1.5 xl:hidden" />
			<main
				ref={scrollRef}
				onScroll={diffMode ? handleScroll : undefined}
				className={cn(
					"relative min-h-0 flex-1",
					diffMode
						? "overflow-y-auto bg-white"
						: "flex flex-col overflow-hidden",
				)}
			>
				<div
					className={cn(
						"relative flex",
						diffMode ? "sticky top-0 z-10" : "min-h-0 flex-1",
					)}
				>
					<SwapButton />
					{PanelSides.map((side, i) => (
						<div
							key={side}
							className={cn(
								"flex min-h-0 min-w-0 flex-1 flex-col",
								side === "left" && "border-gray-200 border-r",
							)}
						>
							<PanelHeader side={side} />
							<DiffEditor side={side} scrollRef={editorRefs[i]} />
						</div>
					))}
				</div>
				<DiffRows />
			</main>
		</div>
	);
}

export default function DiffViewer() {
	return (
		<TabsProvider>
			<SettingsProvider>
				<DiffProvider>
					<FoldProvider>
						<DiffViewerInner />
					</FoldProvider>
				</DiffProvider>
			</SettingsProvider>
		</TabsProvider>
	);
}

/* ═══ Utilities ═══════════════════════════════ */
const cn = (...a: (string | false | null | undefined | 0 | 0n)[]) =>
	a.filter(Boolean).join(" ");
const countLines = (t: string) => (t ? t.split("\n").length : 0);
const genId = () => Math.random().toString(36).slice(2, 9);

const getTabTitle = (tab: Tab): string => {
	const text = tab.left || tab.right;
	const firstLine = text.split("\n")[0]?.trim();
	if (!firstLine) return "Untitled";
	return firstLine.length > TAB_TITLE_MAX
		? `${firstLine.slice(0, TAB_TITLE_MAX)}…`
		: firstLine;
};

function createSafeContext<T>(name: string) {
	const Ctx = createContext<T | null>(null);
	return [
		Ctx.Provider,
		(): T => {
			const v = useContext(Ctx);
			if (v === null) throw new Error(`use${name} requires ${name}Provider`);
			return v;
		},
	] as const;
}

/* ═══ Diff Helpers ════════════════════════════ */
function findBlocks(
	lines: DiffLine[],
	pred: (t: LineType) => boolean,
	minLen = 0,
): Block[] {
	const blocks: Block[] = [];
	for (let i = 0; i < lines.length; ) {
		const line = lines[i];
		if (!line || !pred(line.type)) {
			i++;
			continue;
		}
		const start = i;
		while (i < lines.length) {
			const l = lines[i];
			if (!l || !pred(l.type)) break;
			i++;
		}
		if (i - start > minLen) blocks.push({ start, end: i });
	}
	return blocks;
}

function findMovedBlocks(result: DiffResult) {
	const { left, right } = result;
	type TextBlock = { start: number; end: number; text: string; range: string };

	const scan = (
		leftType: LineType,
		rightType: LineType,
		source: DiffLine[],
	): TextBlock[] => {
		const blocks: TextBlock[] = [];
		for (let i = 0; i < left.length; ) {
			const l = left[i];
			const r = right[i];
			if (!l || !r || l.type !== leftType || r.type !== rightType) {
				i++;
				continue;
			}
			const start = i;
			const rows: { row: number; text: string; num: number | null }[] = [];
			while (i < left.length) {
				const cl = left[i];
				const cr = right[i];
				if (!cl || !cr || cl.type !== leftType || cr.type !== rightType) break;
				const src = source[i];
				if (src?.text.trim() === "" && rows.length > 0) break;
				if (src) rows.push({ row: i, text: src.text, num: src.num });
				i++;
			}
			while (rows.length > 0 && rows[0]?.text.trim() === "") rows.shift();
			while (rows.length > 0 && rows[rows.length - 1]?.text.trim() === "")
				rows.pop();
			const joined = rows.map((r) => r.text).join("\n");
			if (joined.trim().length > 0) {
				const nums = rows
					.map((r) => r.num)
					.filter((n): n is number => n !== null);
				const firstNum = nums[0];
				const lastNum = nums[nums.length - 1];
				if (firstNum !== undefined && lastNum !== undefined) {
					const range =
						firstNum === lastNum
							? `line ${firstNum}`
							: `lines ${firstNum}–${lastNum}`;
					blocks.push({
						start: rows[0]?.row ?? start,
						end: (rows[rows.length - 1]?.row ?? i - 1) + 1,
						text: joined,
						range,
					});
				}
			}
		}
		return blocks;
	};

	const normalize = (s: string) =>
		s
			.split("\n")
			.map((l) => l.trim())
			.filter((l) => l.length > 0)
			.join("\n");
	const removals = scan("removed", "spacer", left);
	const additions = scan("spacer", "added", right);
	const moved: {
		removalStart: number;
		removalEnd: number;
		additionStart: number;
		additionEnd: number;
		originalRange: string;
		modifiedRange: string;
	}[] = [];
	const used = new Set<number>();

	for (const rem of removals) {
		const remNorm = normalize(rem.text);
		if (remNorm.length === 0) continue;
		for (let j = 0; j < additions.length; j++) {
			if (used.has(j)) continue;
			const add = additions[j];
			if (add && remNorm === normalize(add.text)) {
				moved.push({
					removalStart: rem.start,
					removalEnd: rem.end,
					additionStart: add.start,
					additionEnd: add.end,
					originalRange: rem.range,
					modifiedRange: add.range,
				});
				used.add(j);
				break;
			}
		}
	}
	return moved;
}

function buildRenderItems(
	diff: DiffResult,
	foldable: Block[],
	expanded: Set<number>,
	layout: DiffLayout,
): RenderItem[] {
	const { left, right } = diff;
	const total = left.length;
	const hidden: Array<{ s: number; e: number; bi: number }> = [];
	for (let i = 0; i < foldable.length; i++) {
		if (expanded.has(i)) continue;
		const block = foldable[i];
		if (!block) continue;
		const hs = block.start + CONTEXT;
		const he = block.end - CONTEXT;
		if (he > hs) hidden.push({ s: hs, e: he, bi: i });
	}
	const items: RenderItem[] = [];
	let row = 0;
	let hi = 0;
	while (row < total) {
		const h = hidden[hi];
		if (h && row === h.s) {
			items.push({ kind: "fold", idx: h.bi, count: h.e - h.s });
			row = h.e;
			hi++;
			continue;
		}
		const l = left[row];
		if (!l) {
			row++;
			continue;
		}
		if (layout === "split" || l.type === "equal") {
			items.push({ kind: "row", idx: row });
			row++;
		} else {
			const start = row;
			while (row < total) {
				const cl = left[row];
				if (!cl || cl.type === "equal") break;
				if (row === hidden[hi]?.s) break;
				row++;
			}
			for (let i = start; i < row; i++)
				if (left[i]?.type === "removed")
					items.push({ kind: "row", idx: i, side: "left" as PanelSide });
			for (let i = start; i < row; i++)
				if (right[i]?.type === "added")
					items.push({ kind: "row", idx: i, side: "right" as PanelSide });
		}
	}
	return items;
}

/* ═══ Diff Algorithm ══════════════════════════ */
function lcs<T>(a: T[], b: T[]): number[][] {
	const m = a.length;
	const n = b.length;
	const dp: number[][] = Array.from({ length: m + 1 }, () =>
		Array<number>(n + 1).fill(0),
	);
	for (let i = 1; i <= m; i++) {
		const row = dp[i];
		const prev = dp[i - 1];
		if (!row || !prev) continue;
		for (let j = 1; j <= n; j++)
			row[j] =
				a[i - 1] === b[j - 1]
					? (prev[j - 1] ?? 0) + 1
					: Math.max(prev[j] ?? 0, row[j - 1] ?? 0);
	}
	return dp;
}

function mergeSegments(tokens: string[], highlights: boolean[]): Segment[] {
	return tokens.reduce<Segment[]>((acc, text, i) => {
		const last = acc[acc.length - 1];
		if (last && last.hl === highlights[i]) last.text += text;
		else acc.push({ id: i, text, hl: highlights[i] ?? false });
		return acc;
	}, []);
}

function wordDiff(
	oldStr: string,
	newStr: string,
	precision: DiffPrecision = "word",
): { old: Segment[]; new: Segment[] } {
	const tokenize =
		precision === "letter"
			? (s: string) => [...s]
			: (s: string) => s.split(/(\s+)/).filter((t) => t.length);
	const oT = tokenize(oldStr);
	const nT = tokenize(newStr);
	const dp = lcs(oT, nT);
	const oH = Array<boolean>(oT.length).fill(true);
	const nH = Array<boolean>(nT.length).fill(true);
	for (let i = oT.length, j = nT.length; i > 0 && j > 0; ) {
		if (oT[i - 1] === nT[j - 1]) {
			oH[--i] = false;
			nH[--j] = false;
		} else if ((dp[i]?.[j - 1] ?? 0) >= (dp[i - 1]?.[j] ?? 0)) j--;
		else i--;
	}
	return { old: mergeSegments(oT, oH), new: mergeSegments(nT, nH) };
}

function computeDiff(
	oldText: string,
	newText: string,
	precision: DiffPrecision = "word",
): DiffResult {
	type DiffOp =
		| { kind: "="; li: number; ri: number }
		| { kind: "+"; ri: number }
		| { kind: "-"; li: number };
	const a = oldText.split("\n");
	const b = newText.split("\n");
	const dp = lcs(a, b);
	const ops: DiffOp[] = [];
	for (let i = a.length, j = b.length; i > 0 || j > 0; ) {
		if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
			ops.unshift({ kind: "=", li: i - 1, ri: j - 1 });
			i--;
			j--;
		} else if (
			j > 0 &&
			(i === 0 || (dp[i]?.[j - 1] ?? 0) >= (dp[i - 1]?.[j] ?? 0))
		) {
			ops.unshift({ kind: "+", ri: j - 1 });
			j--;
		} else {
			ops.unshift({ kind: "-", li: i - 1 });
			i--;
		}
	}
	const left: DiffLine[] = [];
	const right: DiffLine[] = [];
	let added = 0;
	let removed = 0;
	let ln = 0;
	let rn = 0;
	for (let idx = 0; idx < ops.length; ) {
		const op = ops[idx];
		if (!op) break;
		if (op.kind === "=") {
			left.push({ text: a[op.li] ?? "", type: "equal", num: ++ln });
			right.push({ text: b[op.ri] ?? "", type: "equal", num: ++rn });
			idx++;
		} else {
			const rem: number[] = [];
			const add: number[] = [];
			while (idx < ops.length) {
				const cur = ops[idx];
				if (!cur || cur.kind === "=") break;
				cur.kind === "-" ? rem.push(cur.li) : add.push(cur.ri);
				idx++;
			}
			const paired = Math.min(rem.length, add.length);
			for (let k = 0; k < paired; k++) {
				const ri = rem[k];
				const ai = add[k];
				if (ri === undefined || ai === undefined) continue;
				const oldLine = a[ri] ?? "";
				const newLine = b[ai] ?? "";
				const wd = wordDiff(oldLine, newLine, precision);
				left.push({ text: oldLine, type: "removed", num: ++ln, segs: wd.old });
				right.push({ text: newLine, type: "added", num: ++rn, segs: wd.new });
			}
			for (let k = paired; k < rem.length; k++) {
				const t = a[rem[k] ?? 0] ?? "";
				left.push({
					text: t,
					type: "removed",
					num: ++ln,
					segs: t ? [{ id: `${idx}-${k}-rem`, text: t, hl: true }] : undefined,
				});
				right.push({ text: "", type: "spacer", num: null });
			}
			for (let k = paired; k < add.length; k++) {
				const t = b[add[k] ?? 0] ?? "";
				left.push({ text: "", type: "spacer", num: null });
				right.push({
					text: t,
					type: "added",
					num: ++rn,
					segs: t ? [{ id: `${idx}-${k}-add`, text: t, hl: true }] : undefined,
				});
			}
			removed += rem.length;
			added += add.length;
		}
	}
	return { left, right, added, removed };
}
